<script>
	import { onMount } from 'svelte';
	import { afterNavigate } from '$app/navigation';
	import { authStore } from '$lib/stores/authStore.js';
	import { page } from '$app/stores';
	import { hasUnread, checkUnread } from '$lib/stores/announcementStore.js';
	import {
		hasUnread as hasUnreadInquiryReply,
		checkUnread as checkInquiryReplyUnread
	} from '$lib/stores/inquiryReplyStore.js';
	import { startUnreadPolling } from '$lib/utils/unreadPolling.js';

	$: isLoggedIn = $authStore.isLoggedIn;

	$: if (isLoggedIn) {
		checkUnread();
		checkInquiryReplyUnread();
	}
	$: currentPath = $page.url.pathname;

	onMount(() =>
		startUnreadPolling([checkUnread, checkInquiryReplyUnread], () => $authStore.isLoggedIn)
	);

	// SPA 네비게이션 시 미확인 알림 재조회
	afterNavigate(() => {
		if (!$authStore.isLoggedIn) return;
		checkUnread();
		checkInquiryReplyUnread();
	});
</script>

<div class="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-white to-indigo-50">
	<!-- Beta Banner -->
	<div class="fixed top-0 left-0 right-0 z-50 bg-blue-600 text-white text-center py-2 px-4 text-sm">
		내부 테스트 중입니다.
	</div>

	<!-- Header -->
	<header
		class="fixed top-8 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200"
	>
		<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
			<div class="flex items-center justify-between h-16">
				<!-- Logo -->
				<a href="/" class="flex items-center gap-2">
					<img src="/logo.png" alt="단비데스크 로고" class="w-[1.875rem] h-[1.875rem]" />
					<span class="text-xl font-bold" style="color: #3F5264;">단비데스크</span>
				</a>

				<!-- Navigation -->
				<nav class="flex items-center gap-2">
					{#if isLoggedIn}
						<a
							href="/dashboard/announcements"
							class="relative p-2 {currentPath.startsWith('/dashboard/announcements')
								? 'text-blue-600'
								: 'text-gray-700'} hover:text-gray-800 transition-colors"
							title="공지사항"
						>
							<svg
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								stroke-width="1.5"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
								/>
							</svg>
							{#if $hasUnread}
								<span class="absolute top-1.5 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
							{/if}
						</a>
						<a
							href="/dashboard/inquiries"
							class="relative px-4 py-2 text-sm font-medium {currentPath.startsWith(
								'/dashboard/inquiries'
							)
								? 'text-blue-600'
								: 'text-gray-700'} hover transition-colors"
						>
							1:1 문의
							{#if $hasUnreadInquiryReply}
								<span
									class="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"
									aria-label="새 답변 있음"
								></span>
							{/if}
						</a>
					{/if}
					<a
						href="/faq"
						class="px-4 py-2 text-sm font-medium {currentPath === '/faq'
							? 'text-blue-600'
							: 'text-gray-700'} hover transition-colors"
					>
						FAQ
					</a>

					{#if isLoggedIn}
						<a
							href="/dashboard"
							class="px-4 py-2 text-sm font-medium text-gray-700 hover transition-colors"
						>
							대시보드
						</a>
					{:else}
						<a
							href="/auth/login"
							class="px-4 py-2 text-sm font-medium text-gray-700 hover transition-colors"
						>
							로그인
						</a>
					{/if}
				</nav>
			</div>
		</div>
	</header>

	<!-- Main Content -->
	<div class="flex-1">
		<slot />
	</div>

	<!-- Footer -->
	<footer class="py-10 px-4 sm:px-6 lg:px-8 border-t border-gray-200 bg-gray-50">
		<div class="max-w-7xl mx-auto">
			<div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
				<div class="text-sm text-gray-500 space-y-1">
					<p class="font-medium text-gray-700">텍스트먼트(Textment)</p>
					<p>사업자등록번호: 866-06-03530</p>
					<p>주소: 대전광역시 서구 둔산로 137번길 21, 3038호(둔산동, 대승빌딩)</p>
					<p>대표자명: 목봉균</p>
					<p>
						전화: <a href="tel:0506-016-1680" class="hover:text-gray-700 transition-colors"
							>0506-016-1680</a
						>
					</p>
					<p>
						이메일: <a
							href="mailto:contact@danbidesk.com"
							class="hover:text-gray-700 transition-colors">contact@danbidesk.com</a
						>
					</p>
				</div>
				<div class="flex gap-4 text-sm">
					<a
						href="/policy/terms"
						target="_blank"
						rel="noopener noreferrer"
						class="text-gray-500 hover:text-gray-700 transition-colors">이용약관</a
					>
					<a
						href="/policy/privacy"
						target="_blank"
						rel="noopener noreferrer"
						class="text-gray-500 hover:text-gray-700 transition-colors">개인정보 처리방침</a
					>
				</div>
			</div>
			<p class="text-sm text-gray-400">&copy; 2025 단비데스크. All rights reserved.</p>
		</div>
	</footer>
</div>
